import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import RelifLogo from 'assets/images/logo.webp';
import BadgeZeller from 'assets/images/z-blanca-fondo-azul.png';
import { PrimaryBtn } from 'components/UI/Buttons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LinkButton = styled(Button)(({ theme }) => ({
  color: '#15183A',
  fontSize: '17px',
  fontFamily: 'Roboto',
  padding: '6px 8px',
  marginLeft: '8px',
  marginRight: '8px',
  display: 'inline-flex',
  justifyContent: 'center',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&:before': {
    content: "''",
    position: 'absolute',
    height: '3px',
    bottom: '4px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: theme.palette.yellow.main,
    visibility: 'hidden',
    transition: 'all 0.2s ease',
    width: '0',
  },
  '&:hover:before': {
    visibility: 'visible',
    width: 'calc(100% - 16px)', // Subtracting the left and right margin
  },
}));

const Navigation = () => {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(false);

  const renderNavigationLinks = () => {
    return (
      <>
        <LinkButton onClick={() => navigate('/quienes-somos')}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.9em',
              lineHeight: '25px',
            }}
          >
            Quiénes somos
          </Typography>
        </LinkButton>
        <LinkButton onClick={() => navigate('/casos-de-exito')}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.9em',
              lineHeight: '25px',
            }}
          >
            Casos de éxito
          </Typography>
        </LinkButton>
      </>
    );
  };

  const renderMobileNavigationLinks = () => (
    <>
      <Button
        variant="text"
        onClick={() => {
          navigate('/quienes-somos');
          setOpenMenu(false);
        }}
        sx={{
          fontFamily: 'Jost',
          fontWeight: 600,
          fontSize: '15px',
          lineHeight: '25px',
          justifyContent: 'flex-start',
          paddingLeft: 0,
          textAlign: 'left',
          width: '100%',
          mb: 0,
          color: 'primaryDark.main',
        }}
      >
        Quiénes somos
      </Button>
      <Button
        variant="text"
        onClick={() => {
          navigate('/casos-de-exito');
          setOpenMenu(false);
        }}
        sx={{
          fontFamily: 'Jost',
          fontWeight: 600,
          fontSize: '15px',
          lineHeight: '25px',
          justifyContent: 'flex-start',
          paddingLeft: 0,
          textAlign: 'left',
          width: '100%',
          mb: 2,
          color: 'primaryDark.main',
        }}
      >
        Casos de éxito
      </Button>
    </>
  );

  return (
    <AppBar
      sx={{
        variant: 'h2',
        backgroundColor: 'white',
        boxShadow: 'none',
      }}
    >
      <Container sx={{ maxWidth: { xs: '100%', sm: '100%', md: '100%', lg: '90%', xl: '85%' } }}>
        <Box
          sx={{
            py: 0.5,
            my: 0.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
            <img
              src={RelifLogo}
              alt="Logo"
              style={{
                maxWidth: '7em',
              }}
            />
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
            }}
          >
            <Box
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                display: 'flex',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mr: 2,
                }}
              >
                {renderNavigationLinks()}
              </Box>
              <PrimaryBtn
                onClick={() => {
                  navigate('/sign-in');
                }}
                sx={{
                  backgroundColor: 'yellow.main',
                  color: 'primaryDark.main',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#DCB705',
                  },
                }}
              >
                Iniciar sesión
              </PrimaryBtn>
            </Box>
          </Box>
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
            }}
          >
            <IconButton onClick={() => setOpenMenu(true)}>
              <MenuIcon sx={{ color: 'grey', fontSize: '32px' }} />
            </IconButton>
            <SwipeableDrawer
              open={openMenu}
              onClose={() => setOpenMenu(false)}
              onOpen={() => setOpenMenu(true)}
              anchor="left"
              sx={{
                '& .MuiDrawer-paper': {
                  width: { xs: '280px', sm: '350px' },
                },
              }}
            >
              <Stack
                spacing={0}
                sx={{
                  alignItems: 'start',
                  py: 4,
                  px: 3,
                }}
              >
                {/* BadgeRelif and Close button side by side */}
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 0 }}
                >
                  <img
                    src={BadgeZeller}
                    alt="Relif Badge"
                    style={{
                      height: '70px',
                      width: '40px',
                      objectFit: 'cover',
                    }}
                  />
                  <IconButton
                    onClick={() => setOpenMenu(false)}
                    sx={{
                      color: 'grey.600',
                      '&:hover': { backgroundColor: 'grey.100' },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                {renderMobileNavigationLinks()}
                <PrimaryBtn
                  variant="outlined"
                  sx={{
                    color: 'black',
                    backgroundColor: '#F9D00A',
                    borderColor: '#F9D00A',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: '#DCB705',
                      borderColor: '#DCB705',
                      color: 'black',
                    },
                  }}
                  onClick={() => {
                    navigate('/sign-in');
                  }}
                >
                  Iniciar sesión
                </PrimaryBtn>
              </Stack>
            </SwipeableDrawer>
          </Box>
        </Box>
      </Container>
    </AppBar>
  );
};

export default Navigation;
